import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Tabs, Tab, Box, IconButton, Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useState } from 'react';
import { TeacherRulesTab } from './rules/TeacherRulesTab';
import { SubjectRulesTab } from './rules/SubjectRulesTab';
import { PriorityWeightsTab } from './rules/PriorityWeightsTab';

// ============================================================================
// Interfaces
// ============================================================================

export interface TeacherRules {
  maxPeriodsPerDay: number;
  maxPeriodsPerWeek: number;
  maxConsecutivePeriods: number;
}

export interface SubjectRules {
  maxPeriodsPerDayPerClass: number;
  distributeEvenly: boolean;
}

export interface PriorityWeights {
  workloadBalance: number;
  teacherClassAssociation: number;
  subjectQualification: number;
  subjectContinuity: number;
  avoidConsecutive: number;
}

export interface SchoolRules {
  teacherRules: TeacherRules;
  subjectRules: SubjectRules;
  priorityWeights: PriorityWeights;
}

export interface RulesConfigurationDialogProps {
  open: boolean;
  onClose: () => void;
  schoolId: string;
  initialRules?: SchoolRules;
  onSave: (rules: SchoolRules) => void;
}

// ============================================================================
// Defaults
// ============================================================================

const DEFAULT_RULES: SchoolRules = {
  teacherRules: {
    maxPeriodsPerDay: 8,
    maxPeriodsPerWeek: 40,
    maxConsecutivePeriods: 3,
  },
  subjectRules: {
    maxPeriodsPerDayPerClass: 2,
    distributeEvenly: true,
  },
  priorityWeights: {
    workloadBalance: 5,
    teacherClassAssociation: 8,
    subjectQualification: 10,
    subjectContinuity: 8,
    avoidConsecutive: 4,
  },
};

// ============================================================================
// Tab Panel
// ============================================================================

function TabPanel({ children, value, index }: { children?: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`rules-tabpanel-${index}`}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// ============================================================================
// Dialog
// ============================================================================

export function RulesConfigurationDialog({
  open,
  onClose,
  initialRules,
  onSave,
}: RulesConfigurationDialogProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [rules, setRules] = useState<SchoolRules>(initialRules ?? DEFAULT_RULES);

  // Reset local state when dialog opens with new initialRules
  const handleOpen = () => {
    setRules(initialRules ?? DEFAULT_RULES);
    setActiveTab(0);
  };

  const handleSave = () => {
    onSave(rules);
    onClose();
  };

  const handleCancel = () => {
    setRules(initialRules ?? DEFAULT_RULES);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEnter: handleOpen }}
      PaperProps={{ sx: { minHeight: '520px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>
            School Rules Configuration
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Rules applied by auto-fill and the teacher suggestion algorithm
        </Typography>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          aria-label="rules configuration tabs"
        >
          <Tab label="Teacher Rules" id="rules-tab-0" />
          <Tab label="Subject Rules" id="rules-tab-1" />
          <Tab label="Priority Weights" id="rules-tab-2" />
        </Tabs>
      </Box>

      <DialogContent>
        <TabPanel value={activeTab} index={0}>
          <TeacherRulesTab
            rules={rules.teacherRules}
            onChange={(teacherRules) => setRules((prev) => ({ ...prev, teacherRules }))}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <SubjectRulesTab
            rules={rules.subjectRules}
            onChange={(subjectRules) => setRules((prev) => ({ ...prev, subjectRules }))}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <PriorityWeightsTab
            weights={rules.priorityWeights}
            onChange={(priorityWeights) => setRules((prev) => ({ ...prev, priorityWeights }))}
          />
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Rules</Button>
      </DialogActions>
    </Dialog>
  );
}
